import { getRoutesData } from './api'

export interface LatLng {
  lat: number
  lng: number
}

export interface PlannedStop {
  stopCode: string
  name: string
  nameEng: string
  lat: number
  lng: number
  order: number
}

export interface TripOption {
  lineId: string
  lineCode: string
  routeCode: string
  routeDescr: string
  routeDescrEng: string
  board: PlannedStop
  alight: PlannedStop
  walkToBoardM: number
  walkFromAlightM: number
  stopsCount: number
}

export interface Place {
  name: string
  lat: number
  lng: number
}

interface StaticRouteStop {
  order: number
  stopCode: string
  stopDescr: string
  stopDescrEng: string
  lat: number
  lng: number
}

interface StaticRoute {
  routeCode: string
  lineCode: string
  lineId: string
  routeDescr: string
  routeDescrEng: string
  stops: StaticRouteStop[]
}

export function distanceMeters(a: LatLng, b: LatLng): number {
  const R = 6371000
  const dLat = (b.lat - a.lat) * Math.PI / 180
  const dLng = (b.lng - a.lng) * Math.PI / 180
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

/** Rough walking time at ~4.8 km/h */
export function walkMinutes(meters: number): number {
  return Math.max(1, Math.round(meters / 80))
}

function toPlannedStop(stop: StaticRouteStop): PlannedStop {
  return {
    stopCode: stop.stopCode,
    name: stop.stopDescr,
    nameEng: stop.stopDescrEng,
    lat: stop.lat,
    lng: stop.lng,
    order: stop.order,
  }
}

/**
 * Find direct (no transfer) bus options between two points using the bundled
 * route/stop data. Each route contributes its best board/alight pair where the
 * alight stop comes after the board stop. Results are ranked by total walking,
 * plus a small penalty per stop ridden, and de-duplicated per line.
 */
export function planDirectTrips(from: LatLng, to: LatLng, maxWalkM = 900, limit = 5): TripOption[] {
  const routes = (getRoutesData().routes || []) as StaticRoute[]
  const options: (TripOption & { score: number })[] = []

  for (const route of routes) {
    const stops = route.stops || []
    if (stops.length < 2) continue

    const fromDist = stops.map(s => distanceMeters(from, s))
    const toDist = stops.map(s => distanceMeters(to, s))

    let best: { i: number; j: number; score: number } | null = null
    for (let i = 0; i < stops.length - 1; i++) {
      if (fromDist[i] > maxWalkM) continue
      for (let j = i + 1; j < stops.length; j++) {
        if (toDist[j] > maxWalkM) continue
        const score = fromDist[i] + toDist[j] + (j - i) * 25
        if (!best || score < best.score) best = { i, j, score }
      }
    }
    if (!best) continue

    options.push({
      lineId: route.lineId,
      lineCode: route.lineCode,
      routeCode: route.routeCode,
      routeDescr: route.routeDescr,
      routeDescrEng: route.routeDescrEng,
      board: toPlannedStop(stops[best.i]),
      alight: toPlannedStop(stops[best.j]),
      walkToBoardM: Math.round(fromDist[best.i]),
      walkFromAlightM: Math.round(toDist[best.j]),
      stopsCount: best.j - best.i,
      score: best.score,
    })
  }

  options.sort((a, b) => a.score - b.score)
  const seenLines = new Set<string>()
  const result: TripOption[] = []
  for (const { score: _score, ...option } of options) {
    if (seenLines.has(option.lineId)) continue
    seenLines.add(option.lineId)
    result.push(option)
    if (result.length >= limit) break
  }
  return result
}

/** Search places with OpenStreetMap Nominatim, biased to the Athens area. */
export async function searchPlaces(query: string, signal?: AbortSignal): Promise<Place[]> {
  const params = new URLSearchParams({
    q: query,
    format: 'jsonv2',
    limit: '6',
    countrycodes: 'gr',
    viewbox: '23.55,38.15,24.10,37.75',
    bounded: '1',
  })
  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
    signal,
    headers: { 'Accept-Language': 'el,en' },
  })
  if (!res.ok) throw new Error(`Place search failed (${res.status}).`)
  const data = await res.json() as { display_name: string; lat: string; lon: string }[]
  return data.map(d => ({ name: d.display_name, lat: parseFloat(d.lat), lng: parseFloat(d.lon) }))
}
